import { useState } from "react";
import { CheckCircle2, CheckSquare, Circle, Loader2, Square } from "lucide-react";
import { PollOption, PollProps } from "../../../global-components/src/types";
import "./poll.css";
import { useAuth, useMessage } from "../../../global-components/src";
import { errorMessage } from "../../../global-components/src/utils/text";
import axios from "axios";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";

const PostPoll: React.FC<PollProps> = ({ poll }) => {
    const [pollData, setPollData] = useState(poll);
    const [loading, setLoading] = useState(false);
    const { setMessage } = useMessage();
    const { token } = useAuth();

    const totalVotes = pollData.options.reduce(
        (sum: number, option: PollOption) => sum + (option.votes?.length || 0),
        0
    );
    const hasVoted = pollData.options.some((option: PollOption) => !!option.user_vote);

    async function vote(optionId: number) {
        if (loading) return;

        setLoading(true);

        try {
            const { data } = await axios.post(`${PACOCA_API_URL}/poll/vote`, { poll_id: pollData.id, option_id: optionId }, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            setPollData(data);
        } catch (err: any) {
            const messageError = errorMessage(err);
            const reason = err?.response?.data.reason && err?.response?.status == 403 ? err?.response?.data.reason : null;
            setMessage(messageError || "Erro ao enviar o aviso.", "error", undefined, undefined, reason);
            console.error("Erro ao votar agora:", err);
            console.error(messageError);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="card card-poll mt-2">
            <strong className="poll-question">{pollData.question}</strong>

            <div className="poll-options" role="group" aria-label={pollData.question}>
                {pollData.options.map((option: PollOption) => {
                    const userVoted = !!option.user_vote;
                    const votesCount = option.votes?.length || 0;
                    const percent = totalVotes
                        ? Math.round((votesCount / totalVotes) * 100)
                        : 0;

                    return (
                        <button
                            key={option.id}
                            type="button"
                            className={`poll-option${userVoted ? " is-selected" : ""}`}
                            onClick={() => vote(option.id)}
                            disabled={loading}
                            aria-pressed={userVoted}
                        >
                            {hasVoted && (
                                <span
                                    className="poll-progress"
                                    style={{ width: `${percent}%` }}
                                    aria-hidden="true"
                                />
                            )}

                            <span className="poll-option-content">
                                <span className="poll-option-label-wrapper">
                                    {pollData.multiple_choice ? (
                                        userVoted ? (
                                            <CheckSquare className="poll-option-icon" aria-hidden="true" />
                                        ) : (
                                            <Square className="poll-option-icon" aria-hidden="true" />
                                        )
                                    ) : userVoted ? (
                                        <CheckCircle2 className="poll-option-icon" aria-hidden="true" />
                                    ) : (
                                        <Circle className="poll-option-icon" aria-hidden="true" />
                                    )}

                                    <span className="poll-option-label">{option.option_text}</span>
                                </span>

                                {hasVoted && (
                                    <span className="poll-option-percent">{percent}%</span>
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="poll-footer">
                {loading ? (
                    <Loader2 className="poll-loading" size={16} aria-label="Carregando" />
                ) : (
                    <small>{totalVotes} {totalVotes === 1 ? "voto" : "votos"}</small>
                )}
            </div>
        </div>
    );
};

export default PostPoll;
